import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ChevronDown, LogOut, User, ArrowLeftRight, Settings, Building2, ClipboardCheck, UserSearch, Search, LogOut as LogOutIcon } from "lucide-react"
import { Link, useNavigate } from "react-router"
import { useAuth } from "./auth-provider"
import { Avatar, AvatarFallback } from "./ui/avatar"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu"
import { Input } from "./ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import useRequest from '@/modules/shared/hooks/use-request'
import { toast } from "sonner"
import type { LucideIcon } from "lucide-react"

const LUCIDE_MAP: Record<string, LucideIcon> = {
  ChevronDown,
  User,
  ArrowLeftRight,
  Settings,
  Building2,
  ClipboardCheck
}
void LUCIDE_MAP

type EmailItem = {
  email: string
  nome?: string | null
  papel?: string | null
  escola?: string | null
  id_escola?: string | number | null
}

const LIMITE_PAGINA = 50

const papelBadgeVariant = (papel: string | null | undefined): "default" | "secondary" | "outline" | "success" | "warning" => {
  switch (String(papel || "").toUpperCase()) {
    case "ADM": return "success"
    case "PROFESSOR": return "secondary"
    case "RESPONSAVEL": return "warning"
    case "ALUNO": return "default"
    default: return "outline"
  }
}

const papelLabel = (papel: string | null | undefined): string => {
  const v = String(papel || "").toUpperCase()
  if (v === "RESPONSAVEL") return "Responsável"
  if (!v) return "—"
  return v.charAt(0) + v.slice(1).toLowerCase()
}

const schoolIdMatches = (a: unknown, b: unknown): boolean => {
  if (a === undefined || a === null || b === undefined || b === null) return false
  return String(a) === String(b)
}

export function Header() {
  const navigate = useNavigate()
  const { identity, logout, setSuperSchoolId, isImpersonating, startImpersonate, stopImpersonate } = useAuth()
  const { post } = useRequest()
  const [escolas, setEscolas] = useState<{ id: string | number; nome: string }[]>([])
  const [isLoadingEscolas, setIsLoadingEscolas] = useState(false)
  const efetueiCarregamentoEscolas = useRef(false)

  const [impersonateOpen, setImpersonateOpen] = useState(false)
  const [isLoadingEmails, setIsLoadingEmails] = useState(false)
  const [isLoadingImpersonate, setIsLoadingImpersonate] = useState(false)

  const [itensEmails, setItensEmails] = useState<EmailItem[]>([])
  const [totalEmails, setTotalEmails] = useState(0)
  const [paginaAtual, setPaginaAtual] = useState(1)

  const [buscaEmail, setBuscaEmail] = useState("")
  const [buscaNome, setBuscaNome] = useState("")
  const filtroIdEscolaRef = useRef<unknown>(undefined)
  const filtroEmailRef = useRef<string>("")
  const filtroNomeRef = useRef<string>("")
  const debounceRef = useRef<number | null>(null)
  const carregandoRequestRef = useRef(false)
  const paginaAtualRef = useRef(1)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => { paginaAtualRef.current = paginaAtual }, [paginaAtual])

  const totalPaginas = useMemo(() => Math.max(1, Math.ceil(totalEmails / LIMITE_PAGINA)), [totalEmails])
  const faixaExibida = useMemo(() => {
    if (totalEmails === 0) return { inicio: 0, fim: 0 }
    const inicio = (paginaAtual - 1) * LIMITE_PAGINA + 1
    const fim = Math.min(paginaAtual * LIMITE_PAGINA, totalEmails)
    return { inicio, fim }
  }, [paginaAtual, totalEmails])

  const escolaSelecionadaNome = useMemo(() => {
    if (!identity?.school_id) return null
    const encontrado = escolas.find((e) => schoolIdMatches(e.id, identity.school_id))
    if (encontrado) return encontrado.nome
    return null
  }, [escolas, identity?.school_id])

  const montarPayload = (pagina: number): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      pagina,
      limite: LIMITE_PAGINA,
      sort: "nome",
      order: "asc"
    }
    const idEscolaAtual = filtroIdEscolaRef.current
    if (idEscolaAtual !== undefined && idEscolaAtual !== null) {
      payload.id_escola = idEscolaAtual
    }
    if (filtroEmailRef.current.trim()) payload.email = filtroEmailRef.current.trim()
    if (filtroNomeRef.current.trim()) payload.nome = filtroNomeRef.current.trim()
    return payload
  }

  const carregarPagina = useCallback(async (pagina: number) => {
    if (carregandoRequestRef.current) return
    carregandoRequestRef.current = true
    setIsLoadingEmails(true)
    try {
      const payload = montarPayload(pagina)
      const response = await post("/proc/obter_emails_escola", payload)
      if (response.success && response.data) {
        let dados: EmailItem[] = []
        let total = 0
        const raiz = response.data as any
        if (Array.isArray(raiz)) {
          dados = (raiz as any[]).map((x) => ({
            email: typeof x === "string" ? x : x?.email,
            nome: typeof x === "string" ? null : x?.nome ?? null,
            papel: typeof x === "string" ? null : x?.papel ?? null,
            escola: typeof x === "string" ? null : x?.escola ?? null,
            id_escola: typeof x === "string" ? null : x?.id_escola ?? null
          })).filter((x: EmailItem) => !!x.email)
          total = dados.length
        } else {
          const arr = Array.isArray(raiz.dados) ? raiz.dados : []
          dados = (arr as any[]).map((x) => ({
            email: typeof x === "string" ? x : x?.email,
            nome: typeof x === "string" ? null : x?.nome ?? null,
            papel: typeof x === "string" ? null : x?.papel ?? null,
            escola: typeof x === "string" ? null : x?.escola ?? null,
            id_escola: typeof x === "string" ? null : x?.id_escola ?? null
          })).filter((x: EmailItem) => !!x.email)
          total = Number(raiz.total) || dados.length
        }
        setTotalEmails(total)
        setPaginaAtual(pagina)
        setItensEmails(dados)
      } else {
        setItensEmails([])
        setTotalEmails(0)
        setPaginaAtual(1)
      }
    } catch (err) {
      toast.error("Não foi possível carregar os emails")
      setItensEmails([])
      setTotalEmails(0)
      setPaginaAtual(1)
    } finally {
      carregandoRequestRef.current = false
      setIsLoadingEmails(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetarECarregar = useCallback(() => {
    carregandoRequestRef.current = false
    void carregarPagina(1)
  }, [carregarPagina])

  const irParaPagina = useCallback((pagina: number) => {
    if (carregandoRequestRef.current) return
    const alvo = Math.max(1, Math.min(totalPaginas, pagina))
    void carregarPagina(alvo)
  }, [carregarPagina, totalPaginas])

  useEffect(() => {
    if (identity?.role !== "SUPER" || isImpersonating) return
    if (!identity?.token) return
    if (efetueiCarregamentoEscolas.current && escolas.length > 0) return
    let cancelado = false
    const carregarEscolas = async () => {
      setIsLoadingEscolas(true)
      try {
        const response = await post("/proc/obter_escolas_select", {})
        if (response.success && !cancelado) {
          let raw: Record<string, unknown>[] = []
          const data = response.data as any
          if (Array.isArray(data)) raw = data as Record<string, unknown>[]
          else if (Array.isArray(data?.dados)) raw = data.dados as Record<string, unknown>[]
          const lista = raw.map((o) => ({
            id: (o["id"] as string | number | undefined) ?? (o["value"] as string | number | undefined) ?? "",
            nome:
              ((o["nome"] as string | undefined) ?? "").trim().length > 0
                ? (o["nome"] as string)
                : ((o["descricao"] as string | undefined) ?? "").trim().length > 0
                  ? (o["descricao"] as string)
                  : ((o["label"] as string | undefined) ?? "")
          })).filter((e) => e.nome && String(e.id).trim() !== "")
          setEscolas(lista)
          efetueiCarregamentoEscolas.current = true
        }
      } finally {
        if (!cancelado) setIsLoadingEscolas(false)
      }
    }
    carregarEscolas()
    return () => { cancelado = true }
  }, [identity?.role, identity?.token, isImpersonating, escolas.length])

  useEffect(() => {
    if (identity?.role !== "SUPER" || isImpersonating) return
    const idAnterior = filtroIdEscolaRef.current
    const idNovo = identity?.school_id
    if (idAnterior !== undefined || idNovo !== undefined) {
      if (schoolIdMatches(idAnterior, idNovo)) return
    }
    filtroIdEscolaRef.current = idNovo
    if (impersonateOpen && identity?.school_id) resetarECarregar()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity?.school_id, identity?.role, isImpersonating])

  useEffect(() => {
    if (!impersonateOpen) return
    if (itensEmails.length > 0 && !carregandoRequestRef.current) return
    if (!identity?.school_id) return
    void carregarPagina(1)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonateOpen, identity?.school_id])

  useEffect(() => {
    const valor = buscaEmail
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => {
      filtroEmailRef.current = valor
      resetarECarregar()
    }, 300)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [buscaEmail, resetarECarregar])

  useEffect(() => {
    const valor = buscaNome
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => {
      filtroNomeRef.current = valor
      resetarECarregar()
    }, 300)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [buscaNome, resetarECarregar])

  const executarImpersonate = async (email: string) => {
    if (!email || !startImpersonate) return
    setIsLoadingImpersonate(true)
    try {
      const identityNova = await startImpersonate(email)
      toast.success(`Impersonate iniciado com sucesso: ${email}`)
      const roleNova = String(identityNova?.role ?? "").trim().toUpperCase()
      const precisaSelecionarPerfil =
        roleNova === "RESPONSAVEL" ||
        roleNova === "ALUNO"
      if (precisaSelecionarPerfil) {
        navigate("/profile", { replace: true })
      } else {
        navigate("/", { replace: true })
      }
      setTimeout(() => window.location.reload(), 0)
    } catch (err) {
      toast.error("Não foi possível iniciar o impersonate com este usuário")
    } finally {
      setIsLoadingImpersonate(false)
    }
  }

  const sairImpersonate = () => {
    if (!stopImpersonate) return
    stopImpersonate()
    toast.success("Impersonate finalizado. Você voltou para o usuário SUPER.")
    navigate("/", { replace: true })
    setTimeout(() => window.location.reload(), 0)
  }

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/50 px-3 sm:px-6 py-2 shadow-sm transition-all duration-300">
      <div className="flex items-center justify-between gap-2 max-w-7xl mx-auto min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <img src="/logo_mentis.webp" className="h-10 sm:h-17 animate-float-slow group-hover:scale-105 transition-transform duration-500" alt="Mentis" loading="eager" decoding="async" fetchPriority="high" />
            <span className="hidden sm:inline text-3xl font-bold text-gray-800 font-['Patrick_Hand'] tracking-wide">Mentis</span>
          </Link>
          {isImpersonating && (
            <span className="hidden sm:inline-flex ml-1 items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-200 uppercase tracking-wide shrink-0">
              Modo Impersonate
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">

          {/* ===== Select de escola — visível SÓ no desktop, ao lado do botão Impersonate ===== */}
          {identity?.role === "SUPER" && !isImpersonating && (
            <Select
              disabled={isLoadingEscolas || escolas.length === 0}
              value={identity?.school_id ? String(identity.school_id) : ""}
              onValueChange={(valor) => {
                if (valor === "") setSuperSchoolId(undefined)
                else setSuperSchoolId(valor)
                navigate("/", { replace: true })
                setTimeout(() => window.location.reload(), 0)
              }}
            >
              <SelectTrigger size="sm" className="hidden sm:flex min-w-50 border-gray-200 bg-white/70 hover:bg-white transition-all duration-300 shadow-sm">
                <Building2 className="w-4 h-4 text-gray-500" />
                <SelectValue placeholder={isLoadingEscolas ? "Carregando..." : "Selecione a escola"} />
              </SelectTrigger>
              <SelectContent>
                {escolas.map((escola) => (
                  <SelectItem key={escola.id} value={String(escola.id)}>
                    {escola.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* ===== ÚNICO Popover Impersonate compartilhado mobile/desktop ===== */}
          {identity?.role === "SUPER" && !isImpersonating && (
            <Popover open={impersonateOpen} onOpenChange={(aberto) => {
              if (aberto) {
                setTimeout(() => { scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" }) }, 0)
              }
              setImpersonateOpen(aberto)
            }}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 border-gray-200 bg-white/70 hover:bg-white transition-all duration-300 shadow-sm px-2.5 sm:px-3"
                  disabled={isLoadingImpersonate}
                >
                  <UserSearch className="w-4 h-4 text-emerald-600" />
                  <span className="hidden sm:inline text-sm font-medium text-gray-700">Impersonate</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[92vw] sm:w-[460px] max-w-[460px] p-4 space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <UserSearch className="h-4 w-4 text-emerald-600" />
                    <div className="font-semibold text-gray-800">Selecionar usuário</div>
                  </div>
                </div>

                {/* Select escola SÓ DENTRO do popover no mobile; no desktop ele está fora já */}
                <div className="sm:hidden">
                  <Select
                    disabled={isLoadingEscolas || escolas.length === 0}
                    value={identity?.school_id ? String(identity.school_id) : ""}
                    onValueChange={(valor) => {
                      if (valor === "") setSuperSchoolId(undefined)
                      else setSuperSchoolId(valor)
                      navigate("/", { replace: true })
                      setTimeout(() => window.location.reload(), 0)
                    }}
                  >
                    <SelectTrigger size="sm" className="w-full border-gray-200 bg-white/70">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      <SelectValue placeholder={isLoadingEscolas ? "Carregando..." : "Selecione a escola"} />
                    </SelectTrigger>
                    <SelectContent>
                      {escolas.map((escola) => (
                        <SelectItem key={escola.id} value={String(escola.id)}>
                          {escola.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {identity.school_id ? (
                  <div className="text-xs text-muted-foreground truncate">
                    Escola selecionada:{" "}
                    <span className="font-semibold text-gray-700">
                      {escolaSelecionadaNome ?? `Escola ${String(identity.school_id)}`}
                    </span>
                  </div>
                ) : (
                  <div className="text-xs text-rose-600">Selecione uma escola antes.</div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={buscaEmail}
                      onChange={(e) => setBuscaEmail(e.target.value)}
                      placeholder="Buscar por email..."
                      className="pl-9 h-10 text-sm"
                    />
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={buscaNome}
                      onChange={(e) => setBuscaNome(e.target.value)}
                      placeholder="Buscar por nome..."
                      className="pl-9 h-10 text-sm"
                    />
                  </div>
                </div>

                <div
                  ref={scrollContainerRef}
                  className="max-h-80 overflow-y-auto rounded-md border border-gray-100 divide-y divide-gray-50"
                >
                  {isLoadingEmails ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">Carregando...</div>
                  ) : itensEmails.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">Nenhum usuário encontrado.</div>
                  ) : (
                    itensEmails.map((item) => (
                      <button
                        key={`${item.email}-${String(item.id_escola ?? "x")}-${item.papel ?? ""}`}
                        type="button"
                        disabled={!identity?.school_id}
                        onClick={() => { setImpersonateOpen(false); void executarImpersonate(item.email) }}
                        className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-800 truncate">{item.nome || item.email}</div>
                            <div className="text-xs text-muted-foreground truncate font-mono">{item.email}</div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {item.escola && (
                              <Badge variant="outline" className="text-[11px] max-w-[140px] truncate">{item.escola}</Badge>
                            )}
                            {item.papel && (
                              <Badge variant={papelBadgeVariant(item.papel) as any} className="text-[11px]">
                                {papelLabel(item.papel)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                <div className="flex flex-col gap-2 mt-2 border-t border-gray-100 pt-3">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>
                      Página {totalEmails > 0 ? paginaAtual : 0} de {totalPaginas} — Exibindo {faixaExibida.inicio}-{faixaExibida.fim} de {totalEmails} totais.
                    </span>
                    <span>{isLoadingImpersonate ? "Processando..." : ""}</span>
                  </div>
                  <div className="flex items-center justify-center gap-1.5">
                    <Button type="button" size="sm" variant="outline" disabled={paginaAtual <= 1 || carregandoRequestRef.current || totalEmails === 0} onClick={() => irParaPagina(1)} className="h-8 w-8 !p-0 text-sm font-bold">«</Button>
                    <Button type="button" size="sm" variant="outline" disabled={paginaAtual <= 1 || carregandoRequestRef.current || totalEmails === 0} onClick={() => irParaPagina(paginaAtual - 1)} className="h-8 w-8 !p-0 text-sm font-bold">‹</Button>
                    <div className="px-3 h-8 min-w-[3rem] flex items-center justify-center text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-700 tabular-nums">
                      {totalEmails > 0 ? paginaAtual : "—"}
                    </div>
                    <Button type="button" size="sm" variant="outline" disabled={paginaAtual >= totalPaginas || carregandoRequestRef.current || totalEmails === 0} onClick={() => irParaPagina(paginaAtual + 1)} className="h-8 w-8 !p-0 text-sm font-bold">›</Button>
                    <Button type="button" size="sm" variant="outline" disabled={paginaAtual >= totalPaginas || carregandoRequestRef.current || totalEmails === 0} onClick={() => irParaPagina(totalPaginas)} className="h-8 w-8 !p-0 text-sm font-bold">»</Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {isImpersonating && (
            <Button variant="destructive" size="sm" className="gap-2 shadow-sm shrink-0" onClick={sairImpersonate}>
              <LogOutIcon className="w-4 h-4" />
              <span className="hidden sm:inline text-sm font-semibold">Sair Impersonate</span>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 rounded-full hover:bg-gray-100/50 transition-all duration-300 focus:outline-none border border-transparent hover:border-gray-200 hover:shadow-sm min-w-0">
                <Avatar className="w-8 h-8 sm:w-9 sm:h-9 border-2 border-white shadow-sm shrink-0">
                  <AvatarFallback className={
                    isImpersonating
                      ? "bg-linear-to-br from-amber-400 to-amber-600 text-white font-['Patrick_Hand']"
                      : "bg-linear-to-br from-blue-400 to-blue-600 text-white font-['Patrick_Hand']"
                  }>
                    <User className="h-5 w-5" />
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:inline text-base font-medium font-['Nunito'] text-gray-700 truncate max-w-40">
                  {identity?.email}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-gray-800 truncate">{identity?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{identity?.email}</div>
                  {identity?.impersonated_by && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-200">
                      impersonado por {identity.impersonated_by}
                    </div>
                  )}
                </div>
              </DropdownMenuLabel>
              {identity?.role === "RESPONSAVEL" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/profile")}>
                    <ArrowLeftRight className="mr-2 h-4 w-4" />
                    <span>Trocar perfil</span>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              {(identity?.role === "SUPER" && !isImpersonating) && (
                <DropdownMenuItem
                  disabled={!identity?.school_id}
                  className={identity?.school_id ? "cursor-pointer" : "opacity-60 cursor-not-allowed"}
                  onClick={() => navigate("/super/auditoria")}
                >
                  <ClipboardCheck className="mr-2 h-4 w-4" />
                  <div className="flex flex-col">
                    <span>Auditoria</span>
                    {!identity?.school_id && (
                      <span className="text-[10px] text-muted-foreground font-normal">Selecione uma escola antes</span>
                    )}
                  </div>
                </DropdownMenuItem>
              )}
              {identity?.role === "ADM" && (
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/configuracoes")}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Configurações</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer text-red-600 focus:text-red-600" onClick={logout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
